import moment from 'moment';
import {buildWalletAudience} from './walletAudience';
import {
  buildInvestmentListAudience,
  investmentAudienceToForm,
} from './investmentAudience';
import _ from 'lodash';
import {buildRecipientPayload} from 'components/notificationRecipientSelect/recipientCsv';

/**
 * Transforms notification data from API format to form format
 * @param {Object} notificationData - Notification data from API
 * @returns {Object} Formatted data for form
 */
/**
 * Backend scheduling rules, from the communication handover §2.3: a send must
 * be at least five minutes out, and an expiry — when set — at least
 * twenty-four hours after it.
 */
export const MIN_LEAD_MINUTES = 5;
export const MIN_EXPIRY_HOURS = 24;

/**
 * Quill stores its value as an HTML string. A push body must be plain text, so
 * switching a rich in-app body onto a push channel flattens it rather than
 * shipping markup. `<br>` and closing block tags become spaces so words do not
 * run together.
 *
 * Also the only reliable emptiness test for a Quill document: an empty editor
 * reports `<p><br></p>`, which a `required` rule happily accepts.
 */
export const htmlToPlainText = html => {
  const withBreaks = String(html ?? '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6])>/gi, ' ');
  const el = document.createElement('div');
  el.innerHTML = withBreaks;
  return (el.textContent || '').replace(/\s+/g, ' ').trim();
};

/** The earliest send the backend will accept, with a small safety margin. */
export const earliestSendDate = () =>
  moment().add(MIN_LEAD_MINUTES, 'minutes').add(30, 'seconds');

/**
 * The saved announcement reference, from either shape the service accepts:
 * a top-level `announcementId` or `metadata.announcementID`. Top level wins,
 * as it does on the way in. `metadata` is a JSON scalar and has come back as
 * a string from some callers, so it is parsed defensively.
 */
export const readAnnouncementId = notification => {
  if (notification?.announcementId) return notification.announcementId;
  let metadata = notification?.metadata;
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch (error) {
      return undefined;
    }
  }
  return metadata?.announcementID || undefined;
};

export const transformNotificationToFormData = notificationData => {
  if (!notificationData) return null;

  const notification = Array.isArray(notificationData)
    ? notificationData[0]
    : notificationData;

  return {
    // Basic fields
    title: notification.title || '',
    body: notification.body || '',
    category: Array.isArray(notification.category)
      ? notification.category
      : notification.category
      ? [notification.category]
      : [],
    type: Array.isArray(notification.type)
      ? notification.type
      : notification.type
      ? [notification.type]
      : [],
    class: notification.class || '',

    // Email fields
    emailHeader: notification.emailHeader || '',
    emailBody: notification.body || '', // Assuming body is used for email body
    emailBanner: notification.emailBanner || '',

    // Disclosure
    addDisclosure: notification.addDisclosure || false,
    disclosureType: notification.disclosureType || 'NONE',
    disclosure: notification.disclosure || '',

    // Language
    baseLanguage: notification.language || 'EN',
    multilang: notification.multilang || [],

    // Schedule
    date: notification.schedule?.date
      ? moment(notification.schedule.date).format('YYYY-MM-DD HH:mm:ss')
      : '',
    frequency: notification.schedule?.frequency || '',
    selectedDays: notification.schedule?.selectedDays || [],

    // Destination. The saved record carries the instrument id at the top
    // level; without this the edit form opened with an empty picker and a
    // save would have dropped the destination.
    instrumentId: notification.instrumentId || undefined,

    // Audience
    audience: notification.audience?.category || 'ALL_USERS',

    // Instrument v2 audience filters. Stored under `audience`, but the form
    // keeps them prefixed so they never collide with the destination field.
    ...investmentAudienceToForm(notification.audience),

    // Sender info (for email)
    senderEmail: notification.from || '',
    replyEmail: notification.replyTo || '',

    // External link
    externalLink: notification.externalLink || '',
    linkShouldOpenInTheApp: notification.shouldOpenInApp || false,

    // Metadata
    metadata: notification.metadata || {},

    // Announcement reference. Saved records may carry it top-level or only
    // inside metadata (as `announcementID`), so both are read back.
    announcementId: readAnnouncementId(notification),

    // WhatsApp
    whatsappTemplateName: notification.whatsappTemplateName || '',

    // Determine active tab
    activeTab: notification.category?.includes('EMAIL')
      ? 'EMAIL'
      : notification.category?.includes('WHATSAPP')
      ? 'WHATSAPP'
      : 'APP',
  };
};

/**
 * Transforms form data to API format for creating/updating notifications
 * @param {Object} formValues - Form values
 * @param {Object} additionalData - Additional data from state (body, emailBody, etc.)
 * @returns {Object} Formatted data for API
 */
export const transformFormDataToNotificationPayload = (
  formValues,
  additionalData = {},
) => {
  const {
    body,
    emailBody,
    emailHeader,
    emailImage,
    activeTab,
    addDisclosure,
    date,
    // frequency,
    opportunityMetaData,
    reportMetaData,
    companyMetaData,
    groupMetaData,
    translatedLanguage,
    linkShouldOpenInTheApp,
    selectedWhatsappTemplate,
  } = additionalData;

  // Schedule fields
  const scheduleFields = {};
  // Both fallbacks used to land inside the five-minute window the backend
  // enforces — three minutes for a stale date, ten seconds for a missing one —
  // so a form that looked valid was refused on submit.
  if (date) {
    const chosen = moment(date);
    scheduleFields.date = chosen.isBefore(earliestSendDate())
      ? earliestSendDate().toISOString()
      : chosen.toISOString();
  } else {
    scheduleFields.date = earliestSendDate().toISOString();
  }
  if (!date) scheduleFields.frequency = 'ONE_TIME';
  if (formValues?.frequency) scheduleFields.frequency = formValues?.frequency;
  if (formValues?.selectedDays && formValues?.selectedDays?.length) {
    scheduleFields.selectedDays = formValues?.selectedDays;
  }

  if (formValues?.expires) {
    scheduleFields.expires = new Date(formValues?.expires).toISOString();
  }

  // Optional fields
  const optionalFields = {};
  if (formValues?.audience === 'INVESTORS_IN_A_CAMPAIGN') {
    optionalFields.campaign = formValues?.liveCampaignId;
  }
  if (formValues?.audience === 'INVESTORS_WITH_RESERVATIONS') {
    optionalFields.campaign = formValues?.testCampaignId;
  }
  if (formValues?.externalLink) {
    optionalFields.externalLink = formValues?.externalLink;
    optionalFields.shouldOpenInApp = linkShouldOpenInTheApp;
  }
  if (emailHeader || formValues?.emailHeader) {
    optionalFields.emailHeader = formValues?.emailHeader || emailHeader;
  }
  if (emailImage || formValues?.emailBanner) {
    optionalFields.emailBanner = formValues?.emailBanner || emailImage;
  }
  if (formValues?.type?.length) {
    optionalFields.type = formValues?.type;
  }

  // Signup filter
  let signupFilter = {};
  if (formValues?.signupDate === 'RANGE') {
    signupFilter.signupDateRange = {
      from: formValues?.from ? new Date(formValues?.from).toISOString() : null,
      to: formValues?.to ? new Date(formValues?.to).toISOString() : null,
    };
  }
  if (formValues?.signupDate === 'DURATION') {
    signupFilter.signupDateDuration = {
      value: Number(formValues?.durationValue),
      unit: formValues?.durationUnit,
    };
  }

  // Order optional fields
  let orderOptionalFields = {};
  if (formValues.orderFrom || formValues.orderTo) {
    orderOptionalFields.orderTransactionDate = {
      from: formValues.orderFrom
        ? new Date(formValues.orderFrom).toISOString()
        : null,
      to: formValues.orderTo
        ? new Date(formValues.orderTo).toISOString()
        : null,
    };
  }
  if (formValues?.orderStatus?.length)
    orderOptionalFields.orderStatus = formValues?.orderStatus;
  // Asset class, instrument family, exact instrument, direction and type go
  // out as lists; the notification service resolves the taxonomy of any
  // exact instruments itself.
  Object.assign(orderOptionalFields, buildInvestmentListAudience(formValues));
  if (formValues?.isOrderOnHold)
    orderOptionalFields.isOrderOnHold = formValues?.isOrderOnHold;
  if (formValues?.isAmlApproved)
    orderOptionalFields.isAmlApproved = formValues?.isAmlApproved;
  if (formValues?.isDocumentCheckApproved)
    orderOptionalFields.isDocumentCheckApproved =
      formValues?.isDocumentCheckApproved;
  if (formValues?.isKycApproved)
    orderOptionalFields.isKycApproved = formValues?.isKycApproved;
  if (formValues?.riskProfile)
    orderOptionalFields.riskProfile = formValues?.riskProfile;

  // WhatsApp variables
  const whatsappVariables =
    activeTab === 'WHATSAPP'
      ? buildWhatsappVariables(formValues, selectedWhatsappTemplate)
      : {};

  // Metadata.
  //
  // Report, company-profile, group and legacy-opportunity routes still carry
  // their nested metadata. Instrument routes must not: the backend derives
  // flat `{type, instrumentId}` itself, and the fanout service serialises it.
  // The admin used to hand-build `metadata.bonds` / `metadata.mutualFund`.
  let metaData = {};
  if (!_.isEmpty(companyMetaData)) metaData.company = companyMetaData;
  else if (!_.isEmpty(reportMetaData)) metaData.report = reportMetaData;
  else if (!_.isEmpty(opportunityMetaData)) {
    metaData.opportunity = opportunityMetaData;
  } else if (!_.isEmpty(groupMetaData)) metaData.group = groupMetaData;

  // Base payload
  const basePayload = {
    category: activeTab === 'EMAIL' ? 'EMAIL' : formValues?.category,
    title: formValues?.title,
    body:
      activeTab === 'EMAIL'
        ? formValues?.emailBody || emailBody
        : formValues?.body || body,
    groups: formValues?.groups,
    from: formValues?.senderEmail,
    replyTo: formValues?.replyEmail,
    addDisclosure,
    class: formValues?.class,
    // `assetClass` and `company` are deprecated and rejected at runtime on
    // dev. The destination for every instrument route is `instrumentId`; the
    // backend derives `instrumentKey` and `assetClassKey` from it.
    ...(formValues.instrumentId && {instrumentId: formValues.instrumentId}),
    // DIVIDENDS_ANNOUNCEMENT is not in the Instrument v2 destination matrix
    // and still collects these two. Scoped to that one type so every other
    // route stays clean — it will fail loudly on dev rather than silently
    // losing the fields, which is the state we want it in until product
    // decides whether the type survives.
    ...(formValues.type === 'DIVIDENDS_ANNOUNCEMENT' && {
      ...(formValues.assetClass && {assetClass: formValues.assetClass}),
      ...(formValues.company && {
        company: formValues.company.split(';')[1],
      }),
    }),
    disclosure: formValues?.disclosure,
    ...(formValues.collectionId && {
      collectionId: formValues.collectionId,
    }),
    // Optional announcement reference. The input field is `announcementId`,
    // matching its instrumentId / companyId / collectionId siblings; the
    // service emits it to mobile as `metadata.announcementID`. Neither
    // casing is a typo. It never changes routing.
    ...(formValues.announcementId && {
      announcementId: formValues.announcementId,
    }),
    disclosureType: formValues?.disclosureType,
    metadata: {
      ...metaData,
    },
    ...optionalFields,
    topics: formValues?.topics?.map(data => data.split(';')[1]),
    schedule: {
      ...scheduleFields,
    },
    language: formValues?.baseLanguage,
    multilang:
      translatedLanguage.length > 0
        ? translatedLanguage.map(item => {
            return {
              body: item?.message,
              title: item?.title,
              language: item?.language,
              emailHeader: item?.header ? item?.header : undefined,
            };
          })
        : [],
    audience: {
      ...signupFilter,
      ...orderOptionalFields,
      ...buildWalletAudience(formValues),
      category:
        formValues?.audience === 'USERS'
          ? 'SPECIFIC_USERS'
          : formValues?.audience,
      ...(formValues?.accredited && {
        accredited: formValues?.accredited,
      }),
      ...(formValues?.latestVisit && {lastAppVisit: formValues?.latestVisit}),
      ...(formValues?.isDocumentCheckApproved && {
        isDocumentCheckApproved: formValues?.isDocumentCheckApproved,
      }),
      ...(formValues?.isAmlApproved && {
        isAmlApproved: formValues?.isAmlApproved,
      }),
      ...(formValues?.isKycApproved && {
        isKycApproved: formValues?.isKycApproved,
      }),
      ...(formValues?.isDabaPro && {isDabaPro: formValues?.isDabaPro}),
      ...(formValues?.verifiedInvestors && {
        verifiedInvestors: formValues?.verifiedInvestors,
      }),
      ...(formValues?.activeInvestors && {
        activeInvestor: formValues?.activeInvestors,
      }),
      ...(formValues?.profileCompletion && {
        profileCompletion: formValues?.profileCompletion,
      }),
      state: formValues?.state,
      city: formValues?.city,
      citizenStatus: formValues?.citizenStatus,
      countryOfBirth:
        formValues?.countryOfBirth?.length > 0
          ? formValues?.countryOfBirth?.map(item => item.split(';')[1])
          : formValues?.countryOfBirth,
      countryOfCitizenship:
        formValues?.countryOfCitizenship?.length > 0
          ? formValues?.countryOfCitizenship.map(item => item.split(';')[1])
          : formValues?.countryOfCitizenship,
      country: formValues?.countryOfResidence,
      visaType: formValues?.visaType,
      investmentHorizon: formValues?.investmentHorizon,
      annualNetIncome: formValues?.netIncome,
      employmentStatus: formValues?.employmentStatus,
      taxFilingStatus: formValues?.taxFilingStatus,
      estimatedNetWorth: formValues?.estimatedNetWorth,
      investmentPreference: formValues?.investmentPreference,
      investmentExperience: formValues?.investmentExperience,
      recommendedPortfolioStrategy: formValues?.investmentStrategies,
      affiliations: formValues?.investmentAffiliation,
      investmentGoals: formValues?.investmentGoals,
    },
    ...whatsappVariables,
  };

  // If sending to specific users. `updatedScheduledNotifications` re-sends
  // through the same path as `sendNotificationToUsers`, so it takes the same
  // `userIds` / `userEmails` pair — an edit can add recipients from a sheet
  // without the admin hunting each one down in the picker.
  if (formValues?.audience === 'USERS') {
    return {
      ...basePayload,
      ...buildRecipientPayload(additionalData.recipients),
    };
  }

  return basePayload;
};

/**
 * The WhatsApp half of a notification payload.
 *
 * A template's first variable is always the recipient's username, filled in by
 * the service, so it is dropped here. Link variables are not typed by the
 * admin either — they are rebuilt from the template's own link descriptor.
 *
 * This was written out three times (the composer, the edit transformer and the
 * single-user modal); one copy keeps them from drifting apart.
 */
export const buildWhatsappVariables = (
  formValues,
  selectedWhatsappTemplate,
) => {
  const templateVariables = (multilangValues = null) => [
    ...(selectedWhatsappTemplate?.templateVariables ?? [])
      // Ignore the username variable
      .slice(1)
      .filter(variable => !variable?.isAppLink)
      .map(variable => ({
        name: variable.name,
        value: multilangValues
          ? multilangValues?.[variable.name]
          : formValues[variable.name],
        isAppLink: variable.isAppLink,
      })),
    ...(() => {
      if (selectedWhatsappTemplate?.templateLink?.isStatic) return [];
      const source = multilangValues ?? formValues;
      const isSingleOpportunity =
        selectedWhatsappTemplate?.templateLink?.type === 'SINGLE_OPPORTUNITY';
      return [
        {
          name: 'single-item',
          value: source?.['template-link-id'],
          isAppLink: true,
          campaignMode: isSingleOpportunity
            ? source?.['campaignMode']
            : undefined,
          listingType: isSingleOpportunity
            ? source?.['listingType']
            : undefined,
        },
      ];
    })(),
  ];

  return {
    category: 'WHATSAPP',
    title: formValues['title-template-variable'],
    whatsappTemplateName: selectedWhatsappTemplate?.templateName,
    whatsappTemplateVariable: templateVariables(),
    ...(formValues['whatsappTranslatedLanguage'] && {
      multilang: {
        language: formValues['whatsappTranslatedLanguage'],
        whatsappTemplateName: selectedWhatsappTemplate?.templateName,
        whatsappTemplateVariable: templateVariables(
          formValues?.whatsappMultiLang ?? {},
        ),
      },
    }),
  };
};

/**
 * The payload for a one-off notification aimed at a single user, composed from
 * a row action elsewhere in the admin (a wallet transaction, a swap, an order,
 * a KYC submission).
 *
 * It is the composer's `sendNotificationToUsers` shape with every audience
 * filter removed. The recipient is already decided by the row that was
 * clicked, so there is nothing left to filter on — `userIds` carries exactly
 * one id and `audience.category` says so.
 */
export const buildSingleUserNotificationPayload = ({
  formValues,
  activeTab,
  userId,
  addDisclosure,
  linkShouldOpenInTheApp,
  translations = [],
  metadata = {},
  selectedWhatsappTemplate,
}) => {
  const scheduleFields = {frequency: 'ONE_TIME'};
  // "Send now" is the earliest the backend accepts, not this instant — it
  // refuses anything inside the five-minute lead window.
  if (formValues?.date) {
    const chosen = moment(formValues.date);
    scheduleFields.date = chosen.isBefore(earliestSendDate())
      ? earliestSendDate().toISOString()
      : chosen.toISOString();
  } else {
    scheduleFields.date = earliestSendDate().toISOString();
  }
  if (formValues?.expires) {
    scheduleFields.expires = new Date(formValues.expires).toISOString();
  }

  const optionalFields = {};
  if (formValues?.externalLink) {
    optionalFields.externalLink = formValues.externalLink;
    optionalFields.shouldOpenInApp = Boolean(linkShouldOpenInTheApp);
  }
  if (formValues?.emailHeader) {
    optionalFields.emailHeader = formValues.emailHeader;
  }
  if (formValues?.emailBanner) {
    optionalFields.emailBanner = formValues.emailBanner;
  }
  if (formValues?.type?.length) {
    optionalFields.type = formValues.type;
  }

  const whatsappVariables =
    activeTab === 'WHATSAPP'
      ? buildWhatsappVariables(formValues, selectedWhatsappTemplate)
      : {};

  return {
    category: activeTab === 'EMAIL' ? 'EMAIL' : formValues?.category,
    title: formValues?.title,
    body: activeTab === 'EMAIL' ? formValues?.emailBody : formValues?.body,
    userIds: [userId],
    class: formValues?.class,
    draft: false,
    // Email-only, and harmless elsewhere: the service ignores them when the
    // channel has no sender.
    from: formValues?.senderEmail,
    replyTo: formValues?.replyEmail,
    addDisclosure: Boolean(addDisclosure),
    ...(addDisclosure && {
      disclosure: formValues?.disclosure,
      disclosureType: formValues?.disclosureType,
    }),
    // The destination is the instrument itself; the backend derives its
    // instrument key and asset class from the id.
    ...(formValues?.instrumentId && {instrumentId: formValues.instrumentId}),
    ...(formValues?.collectionId && {collectionId: formValues.collectionId}),
    // DIVIDENDS_ANNOUNCEMENT sits outside the Instrument v2 matrix and still
    // needs its two legacy fields; scoped so no other route sends them.
    ...(formValues?.type === 'DIVIDENDS_ANNOUNCEMENT' && {
      ...(formValues?.assetClass && {assetClass: formValues.assetClass}),
      ...(formValues?.company && {company: formValues.company.split(';')[1]}),
    }),
    metadata: {...metadata},
    ...optionalFields,
    schedule: {...scheduleFields},
    language: formValues?.baseLanguage,
    multilang: translations.map(item => ({
      title: item?.title,
      body: item?.message,
      language: item?.language,
      emailHeader: item?.header || undefined,
    })),
    audience: {category: 'SPECIFIC_USERS'},
    ...whatsappVariables,
  };
};
